import { Box, Typography, useMediaQuery } from "@mui/material";

import heroGlow from "../../../assets/Images/Temples/hero-glow.svg";
import mandala from "../../../assets/Images/Temples/mandala.svg";
import checkIcon from "../../../assets/Images/Temples/check.svg";
import { float, spin } from "./motion";
import MobileHero, { MobileStats } from "./MobileHero";
import TempleArch, { FRAME_HEIGHT, UNIT, leftAt, pct, u } from "./TempleArch";
import { HINDI_FONT, TEMPLE_COLORS, TempleButton } from "../shared";


// Mandala art sits at 2% inset inside its 700px box and turns slowly.
const Mandala = ({ x, y, opacity, reverse }) => (
  <Box
    component="img"
    src={mandala}
    alt=""
    sx={{
      position: "absolute",
      left: leftAt(x + 350, 673.4),
      top: u(y + 14),
      width: u(673.4),
      height: u(673.4),
      opacity,
      pointerEvents: "none",
      animation: `${spin} 90s linear infinite${reverse ? " reverse" : ""}`,
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    }}
  />
);

const StatsCard = ({ stats }) => (
  <Box
    sx={{
      position: "relative",
      zIndex: 1,
      mx: pct(45),
      mt: u(-46),
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: "48px",
      py: "28px",
      background: "#FFFFFF",
      border: "1px solid #EFE4D3",
      borderRadius: "16px",
      boxShadow: "0px 10px 30px 0px rgba(61, 31, 13, 0.06)",
    }}
  >
    {stats.map((stat, index) => (
      <Box key={stat.label} sx={{ display: "contents" }}>
        {index > 0 && (
          <Box sx={{ width: "1px", height: "48px", background: "#EFE4D3", flexShrink: 0 }} />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "26px",
              background: TEMPLE_COLORS.blush,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box component="img" src={stat.icon} alt="" sx={{ maxWidth: 24, maxHeight: 24 }} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "30px", lineHeight: "36px", color: TEMPLE_COLORS.ink }}>
              {stat.value}
            </Typography>
            <Typography sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "20px", color: TEMPLE_COLORS.muted }}>
              {stat.label}
            </Typography>
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
);

const HeroText = ({ hero }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "24px" }}>
    <Typography sx={{ fontFamily: HINDI_FONT, fontSize: "24px", lineHeight: "32px", color: TEMPLE_COLORS.heroRed }}>
      {hero.tagline}
    </Typography>
    <Typography
      component="h1"
      sx={{
        fontWeight: 800,
        fontSize: "clamp(44px, 4.2553cqw, 60px)",
        lineHeight: 1.1667,
        color: TEMPLE_COLORS.ink,
      }}
    >
      {hero.titleLine1}
      <Box component="span" sx={{ display: "block", color: TEMPLE_COLORS.heroRed }}>
        {hero.titleLine2}
      </Box>
    </Typography>
    <Typography sx={{ fontSize: "18px", lineHeight: "30px", color: TEMPLE_COLORS.body }}>
      {hero.description}
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
      <TempleButton link={hero.primaryCta.link} color={TEMPLE_COLORS.heroRed}>
        {hero.primaryCta.label}
      </TempleButton>
      <TempleButton link={hero.secondaryCta.link} outlined>
        {hero.secondaryCta.label}
      </TempleButton>
    </Box>
    <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: "28px", rowGap: "8px", pt: "8px" }}>
      {hero.trustPoints.map((point) => (
        <Box key={point} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box component="img" src={checkIcon} alt="" sx={{ width: 18, height: 18 }} />
          <Typography sx={{ fontWeight: 500, fontSize: "14px", lineHeight: "20px", color: TEMPLE_COLORS.ink }}>
            {point}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

// Gold-outlined arch window holding the hero photo, with the floating badge.
const ArchPhoto = ({ hero, sx }) => (
  <Box sx={{ position: "relative", ...sx }}>
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        border: "1.102px solid #C9952B",
        borderRadius: `${u(198.28)} ${u(198.28)} ${u(17.63)} ${u(17.63)}`,
      }}
    />
    <Box
      component="img"
      src={hero.image}
      alt=""
      sx={{
        position: "absolute",
        left: "3.7%",
        top: "3.125%",
        width: "92.593%",
        height: "93.75%",
        objectFit: "cover",
        borderRadius: `${u(183.59)} ${u(183.59)} ${u(11.75)} ${u(11.75)}`,
      }}
    />
    {hero.badge && (
      <Box
        sx={{
          position: "absolute",
          left: "-9.26%",
          top: "73.44%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "1.5px",
          p: "14.688px",
          background: "#FFFFFF",
          borderRadius: "16px",
          boxShadow: "0px 8.813px 22.031px 0px rgba(77, 26, 0, 0.15)",
          whiteSpace: "nowrap",
          animation: `${float} 4s ease-in-out infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "26.44px", lineHeight: "29.375px", color: TEMPLE_COLORS.ink }}>
          {hero.badge.value}
        </Typography>
        <Typography sx={{ fontWeight: 500, fontSize: "9.55px", lineHeight: "13.219px", color: TEMPLE_COLORS.muted }}>
          {hero.badge.label}
        </Typography>
      </Box>
    )}
  </Box>
);

const TempleHero = ({ hero, stats }) => {
  // Below 1100px the desktop arch would crowd the copy, so the mobile arch
  // (which stretches to any width) takes over.
  const isCompact = useMediaQuery("(max-width: 1099px)");

  if (isCompact) {
    return (
      <>
        <MobileHero hero={hero} />
        <MobileStats stats={stats} />
      </>
    );
  }

  return (
    <Box sx={{ px: "15px", pt: "15px" }}>
      <Box sx={{ containerType: "inline-size" }}>
        <Box sx={{ "--u": UNIT }}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: "25px",
              height: u(FRAME_HEIGHT),
            }}
          >
                <Box
                  component="img"
                  src={heroGlow}
                  alt=""
                  sx={{ position: "absolute", left: leftAt(1069.28, 760), top: u(79), width: u(760), height: u(760), pointerEvents: "none" }}
                />
                <Mandala x={-210} y={350} opacity={0.35} />
                <Mandala x={719} y={109} opacity={0.28} reverse />
                <TempleArch />
                {/* Anchored to the bottom (text starts at y=261 at full width) so
                    it never runs under the stats card when the arch scales down. */}
                <Box sx={{ position: "absolute", left: pct(111), bottom: u(91), width: "min(600px, 50cqw)" }}>
                  <HeroText hero={hero} />
                </Box>
                <ArchPhoto
                  hero={hero}
                  sx={{ position: "absolute", left: leftAt(1069.28, 396.56), top: u(224), width: u(396.56), height: u(470) }}
                />
          </Box>
          <StatsCard stats={stats} />
        </Box>
      </Box>
    </Box>
  );
};

export default TempleHero;
